type QueryScalar = string | number | boolean;

/** Значение query-параметра: `undefined` и `null` пропускаются, массив даёт повтор ключа (`a=1&a=2`). */
export type QueryValue = QueryScalar | readonly QueryScalar[] | null | undefined;

/**
 * Query-параметры запроса. Тип вида `object`, а не `Record`, чтобы принимать интерфейсы из `api.gen.ts`
 * (у них нет индексной сигнатуры).
 */
export type QueryParams = object;

/** Имена параметров пути из шаблона: `"/v1/cards/{cardId}/users/{userId}"` -> `"cardId" | "userId"`. */
export type PathParamNames<Template extends string> = Template extends `${string}{${infer Name}}${infer Rest}`
    ? Name | PathParamNames<Rest>
    : never;

export type PathParams<Template extends string> = Record<PathParamNames<Template>, string | number>;

const PLACEHOLDER = /\{([^}]+)\}/g;

function encodeScalar(value: QueryScalar): string {
    return encodeURIComponent(String(value));
}

/**
 * Query-строка без `?`. Пропускает `undefined`, `null` и пустые массивы; пробел кодируется как `%20`.
 * Списки «через запятую» (`ids` пользователей) сервер принимает строкой: передавайте `ids.join(",")`.
 */
export function buildQuery(query: QueryParams | undefined): string {
    if (!query) return "";

    const parts: string[] = [];
    for (const [key, raw] of Object.entries(query) as [string, QueryValue][]) {
        if (raw === undefined || raw === null) continue;
        const values: readonly QueryScalar[] = Array.isArray(raw) ? raw : [raw as QueryScalar];
        for (const value of values) {
            parts.push(`${encodeURIComponent(key)}=${encodeScalar(value)}`);
        }
    }
    return parts.join("&");
}

/** Добавляет query к пути, который уже может содержать `?` (например, `avatarPath` из DTO). */
export function appendQuery(path: string, query: QueryParams | undefined): string {
    const search = buildQuery(query);
    if (!search) return path;
    return `${path}${path.includes("?") ? "&" : "?"}${search}`;
}

/**
 * Путь API из шаблона OpenAPI: параметры пути экранируются `encodeURIComponent`, query собирается `buildQuery`.
 *
 * @example
 * buildPath("/v1/cards/{cardId}/users/{userId}", { cardId: 5, userId: 7 }, { role: "executor" });
 * // "/v1/cards/5/users/7?role=executor"
 */
export function buildPath<Template extends string>(
    template: Template,
    ...[params, query]: PathParamNames<Template> extends never
        ? [params?: Record<string, never>, query?: QueryParams]
        : [params: PathParams<Template>, query?: QueryParams]
): string {
    const values = (params ?? {}) as Record<string, string | number | undefined>;
    const path = template.replace(PLACEHOLDER, (_, name: string) => {
        const value = values[name];
        if (value === undefined || value === "") {
            throw new Error(`buildPath: не задан параметр пути «${name}» для ${template}`);
        }
        return encodeURIComponent(String(value));
    });
    return appendQuery(path, query);
}
